import Icon from '@ant-design/icons';
import { Modal, message } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import RefreshSvg from '@/components/Icon/svgs/autorenew_black.svg';
import ChatSvg from '@/components/Icon/svgs/chat.svg';
import CloseSvg from '@/components/Icon/svgs/close.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import SensorSvg from '@/components/Icon/svgs/sensors.svg';
import DoneIcon from '@/components/Icon/svgs/todo_done.svg';
import UndoneIcon from '@/components/Icon/svgs/todo_undone.svg';
import type { EventCreateSessionInterface } from '@/components/chat-app/utils/utils';
import { CONTAINER_EVENT } from '@/components/chat-app/utils/utils';
import { HighLight } from '@/components/highLight';
import HomeContext from '@/context/HomeContext';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import type { IChatListItem, IhighLight } from '@/service/note';
import type { IResonance } from '@/service/resonance';
import { createSessionByResonance, getChatResonance } from '@/service/resonance';
import SimplyMD from '@/ui/components/memory-detail/components/attachments/components/SimplyMD';
import { EVENT } from '@/utils/event';
import { isProduct } from '@/utils/host';
import { generateTimeString } from '@/utils/timestamp-format';

import ChatNoteSettings from '../ChatNoteSettings';
import styles from './index.less';

dayjs.extend(durationPlugin);

interface IProps {
  className?: string;
  detail: IChatListItem;
  showAttachNumber?: number;
  origin?: 'list' | 'chat' | 'detail' | 'select' | 'selected' | 'homepage';
  onClick?: () => void;
  operateCb?: () => void;
  cancelSelectCb?: () => void;
  deleteCb?: (delItem: IChatListItem) => void;
  showTime?: boolean;
  showCTD?: boolean;
  selected?: boolean;
  highlightMap?: IhighLight;
  [key: string]: any;
}

function ChatNoteCard(props: IProps): JSX.Element | null {
  const {
    className,
    detail,
    origin = 'list',
    showTime = true,
    showCTD = false,
    onClick: handleClick,
    deleteCb,
    cancelSelectCb,
    selected,
    highlightMap,
    ...others
  } = props;

  const { title, createTime, summary, images } = detail;
  const _images = images?.slice(0, 4);
  const _title = useMemo(() => {
    return highlightMap?.title || title;
  }, [highlightMap, title]);

  const showCreateTime = showTime && createTime;

  const showCTDItem = showCTD && !!detail.latestMessage;
  const showResonanceItem = showCTD && !!detail.latestMessage && !isProduct();
  const [resonanceModalOpen, setResonanceModalOpen] = useState<boolean>(false);
  const [resonanceLoading, setResonanceLoading] = useState<boolean>(false);
  const [resonanceList, setResonanceList] = useState<IResonance[]>([]);

  const [layoutSize] = useStorage('layoutSize');

  const { t } = useTranslation();

  const [isFirstResonanceBtn, setIsFirstResonanceBtn] = useState<boolean>(false);

  const { shadowMindId } = useGenius();

  const { setContext } = useContext(HomeContext);

  // 检查是否是第一个 resonanceBtn
  useLayoutEffect(() => {
    if (showCTD && detail.latestMessage) {
      // 使用 setTimeout 确保 DOM 已经渲染完成
      setTimeout(() => {
        const allResonanceBtns = document.querySelectorAll('.resonanceBtn');

        if (allResonanceBtns.length > 0) {
          // 获取当前组件中的 resonanceBtn 元素
          const currentResonanceBtn = document.getElementById(`resonance-${detail.sessionId}`);

          // 检查当前元素是否是第一个 resonanceBtn
          setIsFirstResonanceBtn(currentResonanceBtn === allResonanceBtns[0]);
        }
      }, 100);
    }
  }, [showCTD, detail]);

  const handleGetResonance = (refresh = false) => {
    if (resonanceLoading) return;

    setResonanceLoading(true);
    getChatResonance({ sessionId: detail.sessionId, refreshSuggest: refresh })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        setResonanceList(res.data.data || []);
      })
      .catch((err) => {
        message.error(err.message || t('error.oops'));
      })
      .finally(() => {
        setResonanceLoading(false);
      });
  };

  const handleResonanceClick = (item: IResonance) => {
    setResonanceModalOpen(false);

    createSessionByResonance({ resonanceId: Number(item.id) })
      .then((res) => {
        if (res.data.code !== 0 || !res.data.data.sessionId) {
          throw new Error(res.data.message);
        }

        setContext({ chatHomepageRoute: undefined, chatCircleRoute: undefined });

        dispatchEvent(new CustomEvent(EVENT.CHANGE_CHAT_STATUS_LAYOUT_TRULY, { detail: 'normal' }));
        dispatchEvent(
          new CustomEvent<EventCreateSessionInterface>(CONTAINER_EVENT.CREATE_SESSION, {
            detail: {
              params: {
                originSessionId: res.data.data.sessionId,
                extParams: {
                  bindMemoryId: item.id,
                  bindMemoryType: 'RESONANCE'
                }
              }
            }
          })
        );
      })
      .catch((err) => {
        message.error(err.message || t('error.oops'));
      });
  };

  useEffect(() => {
    if (resonanceModalOpen) {
      handleGetResonance(detail.hasResonance == true ? false : true);
    }
  }, [resonanceModalOpen, detail]);

  if (origin === 'detail') {
    return (
      <div className={classNames('flex flex-col gap-1', className)}>
        {showCreateTime ? (
          <div className="text-[12px] leading-5 text-[#b8ad9b]">
            {generateTimeString(createTime)}
          </div>
        ) : null}

        <div
          className={classNames(
            styles.container,
            styles.detailContainer,
            'group',
            layoutSize === LAYOUT_SIZE.SMALL ? '!px-5' : ''
          )}
          onClick={handleClick}
          {...others}
        >
          <div className={classNames('flex flex-nowrap gap-3 items-center')}>
            <div className="flex items-center justify-center w-10 h-10 bg-[#FEFDFF] rounded-[44px]">
              <Icon className="w-6 h-6 text-[24px]" component={ChatSvg} />
            </div>
            <span
              className={classNames(
                'text-[14px] leading-[24px] text-[#160A0A] font-medium line-clamp-1'
              )}
            >
              {title || 'Chat'}
            </span>
            <ChatNoteSettings
              className={classNames(
                'absolute ltr:right-5 rtl:left-5 top-[14px] z-10 transition-all duration-200 invisible opacity-0',
                layoutSize === LAYOUT_SIZE.SMALL
                  ? '!visible !opacity-100'
                  : 'group-hover:visible group-hover:opacity-100'
              )}
              detail={detail}
              mindId={shadowMindId}
              onDeleteCb={() => {
                deleteCb && deleteCb(detail);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('flex flex-col gap-1', className)}>
      {showCreateTime ? (
        <div
          className={classNames(
            'text-[12px] leading-5 text-[#b8ad9b]',
            origin === 'chat' ? 'text-center' : ''
          )}
        >
          {generateTimeString(createTime)}
        </div>
      ) : null}
      <div className={classNames(styles.container, 'group')} onClick={handleClick} {...others}>
        <div
          className={classNames('box-content h-6 -mx-5 px-5 flex flex-nowrap gap-2 items-center')}
        >
          <Icon className="w-6 h-6 text-[24px] text-primary-black opacity-80" component={ChatSvg} />
          {_title ? (
            <HighLight className={classNames(styles.shortVoiceContent)}>{_title}</HighLight>
          ) : null}

          {origin !== 'chat' &&
            origin !== 'select' &&
            origin !== 'selected' &&
            origin !== 'homepage' && (
              <ChatNoteSettings
                className={classNames(
                  'absolute ltr:right-5 rtl:left-5 top-[14px] z-10 transition-all duration-200 invisible opacity-0 ',
                  layoutSize === LAYOUT_SIZE.SMALL
                    ? '!visible !opacity-100'
                    : 'group-hover:visible group-hover:opacity-100'
                )}
                detail={detail}
                mindId={shadowMindId}
                onDeleteCb={() => {
                  deleteCb && deleteCb(detail);
                }}
              />
            )}

          {origin === 'select' && (
            <div className={styles.iconWrapper}>
              <Icon
                className={classNames(styles.icon, { [styles.done]: selected })}
                component={selected ? DoneIcon : UndoneIcon}
              />
            </div>
          )}

          {origin === 'selected' && (
            <div
              className={styles.closeBtn}
              onClick={(e) => {
                e.stopPropagation();
                cancelSelectCb?.();
              }}
            >
              <Icon className={styles.icon} component={CloseSvg} />
            </div>
          )}
        </div>
        <div className="-mt-1">
          <SimplyMD
            className={classNames(styles.md, 'text-xs leading-6 text-[#160a0a]')}
            content={summary}
          />
          {_images?.length && (
            <div className="relative h-[60px] mt-3">
              {_images.map((item, index) => {
                return (
                  <img
                    key={index}
                    className={classNames(
                      'w-[140px] min-h-[75px] absolute rounded-[8px] object-cover',
                      index === 0 && 'top-[16px] rotate-[4deg]',
                      index === 1 && `top-[0px] rotate-[2deg] left-[24%]`,
                      index === 2 && `top-[24px] -rotate-[2deg] left-[48%]`,
                      index === 3 && 'top-[48px] right-[0px]',
                      _images.length === 1 && '-top-[0.42px] rotate-[4deg]'
                    )}
                    src={item}
                    style={{
                      boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
      {showCTDItem ? (
        <div className={styles.CTDContainer}>
          <div className={styles.corner} />
          <div
            className={styles.contentContainer}
            onClick={() => {
              handleClick?.();
            }}
          >
            <img
              className="w-[22px] h-[22px] rounded-[50%] shrink-0"
              src="https://mindverseglobal.mindverse.com/front-img/crm/1737439207363/mebot-bio.png"
            />
            <div className="text-sm leading-6 text-[#5d5446] flex-1 line-clamp-1">
              {detail.latestMessage}
            </div>
          </div>
          {showResonanceItem ? (
            isFirstResonanceBtn ? (
              <div
                className={classNames(styles.iconContainer, 'resonanceBtn')}
                id={`resonance-${detail.sessionId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setResonanceModalOpen(true);
                }}
              >
                <Icon className="text-[24px] text-[#756C5D]" component={SensorSvg} />
              </div>
            ) : (
              <div
                className={classNames(styles.iconContainer, 'resonanceBtn')}
                id={`resonance-${detail.sessionId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setResonanceModalOpen(true);
                }}
              >
                <Icon className="text-[24px] text-[#756C5D]" component={SensorSvg} />
              </div>
            )
          ) : null}
        </div>
      ) : null}

      {showCTDItem ? (
        <Modal
          centered
          closable={false}
          destroyOnClose
          footer={null}
          onCancel={() => {
            setResonanceModalOpen(false);
          }}
          open={resonanceModalOpen}
          rootClassName={styles.modalWrapper}
        >
          <div className="bg-[transparent] pb-4 flex items-center gap-3">
            <div className="text-[18px] font-medium leading-8 text-primary-new-black">
              {t('memoryDetail.connectTheDots')}
            </div>
            <div
              className={classNames(
                'ltr:ml-auto rtl:mr-auto flex items-center px-4 py-[6px] gap-2 bg-primary-black rounded-[18px] text-primary-white',
                resonanceLoading ? 'cursor-not-allowed bg-[#ACABB3]' : 'cursor-pointer'
              )}
              onClick={() => {
                if (resonanceLoading) return;

                handleGetResonance(true);
              }}
            >
              <Icon className="text-[16px]" component={RefreshSvg} spin={resonanceLoading} />
              <span className="text-sm leading-6">{t('memoryDetail.refresh')}</span>
            </div>
          </div>
          <div
            className="absolute bottom-0 left-6 w-[calc(100%-48px)] h-[68px] z-[1]"
            style={{
              background: 'linear-gradient(180deg, rgba(220, 214, 224, 0) 0%, #D9D5E1 100%)'
            }}
          />
          {resonanceLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingItem} />
              <div className={styles.loadingItem} />
              <div className={styles.loadingItem} />
              <div className={styles.loadingItem} />
              <div className={styles.loadingItem} />
            </div>
          ) : (
            <div className="flex flex-col gap-4 h-[calc(100%-52px)] overflow-scroll no-scrollbar pb-6 box-border">
              {resonanceList.map((item) => (
                <div
                  key={item.userId}
                  className={styles.resonanceItem}
                  onClick={() => handleResonanceClick(item)}
                >
                  <div className={styles.nameContainer}>
                    <div className={styles.avatar}>
                      <img
                        alt=""
                        className={`w-full h-full ${!item.isFriend ? 'blur-[2px]' : ''}`}
                        src={item.avatar}
                      />
                      <img
                        className="w-full h-full absolute top-0 left-0"
                        src="https://mindverseglobal.mindverse.com/front-img/crm/1769162061121/AIAvatar.png"
                      />
                    </div>
                    <div className={styles.name}>{item.label}</div>
                    <div className={styles.rightContainer}>
                      <Icon className="text-[16px] text-[#B8AD9B]" component={RightSvg} />
                    </div>
                  </div>
                  <div className="text-[#231309] text-sm leading-6 w-full line-clamp-4">
                    {item.greeting}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      ) : null}
    </div>
  );
}

export default ChatNoteCard;
